import { useState, useEffect, useCallback } from 'react';

const DEFAULT_STORAGE_KEY = 'dnd-combat-tracker';

const getStorageKey = (suffix) => (suffix ? `${DEFAULT_STORAGE_KEY}-${suffix}` : DEFAULT_STORAGE_KEY);

const generateId = () => Math.random().toString(36).substr(2, 9);

const getInitialState = () => ({
  combatants: [],
  currentTurnIndex: 0,
  round: 1,
  notes: '',
});

const rollD20 = () => Math.floor(Math.random() * 20) + 1;

/** @param {string} [storageKeySuffix] - '' = personal (dnd-combat-tracker), 'master' = Master Offline (dnd-combat-tracker-master) */
export const useCombat = (storageKeySuffix = '') => {
  const storageKey = getStorageKey(storageKeySuffix);

  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          ...getInitialState(),
          ...parsed,
        };
      }
    } catch (e) {
      console.error('Error loading from localStorage:', e);
    }
    return getInitialState();
  });

  // Save to localStorage on every state change
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  }, [state, storageKey]);

  // Sort combatants by initiative (descending)
  const sortedCombatants = [...state.combatants].sort((a, b) => b.initiative - a.initiative);

  // Get current combatant
  const currentCombatant = sortedCombatants[state.currentTurnIndex] || null;

  // Add combatant
  const addCombatant = useCallback((combatant) => {
    const newCombatant = {
      id: generateId(),
      name: combatant.name,
      hp: combatant.hp,
      maxHp: combatant.maxHp,
      tempHp: combatant.tempHp || 0, // Temporary HP
      ac: combatant.ac || null, // Armor Class
      acVisible: combatant.acVisible !== false, // AC visible to players (default true)
      initiative: combatant.initiative,
      initiativeBonus: typeof combatant.initiativeBonus === 'number' ? combatant.initiativeBonus : 0,
      speed: typeof combatant.speed === 'number' ? combatant.speed : 30,
      spellSlots: combatant.spellSlots || {}, // { [level:number]: { max:number, used:number } }
      statuses: combatant.statuses || [],
      dot: combatant.dot || null, // { damage: number, duration: number | null }
      isKO: combatant.hp <= 0,
      isNPC: combatant.isNPC || false,
      isEnemy: combatant.isEnemy || false, // Enemy (red) or Ally (blue)
      isCompanion: combatant.isCompanion || false, // Companion/Famiglio (green)
      combatantType: combatant.combatantType || 'player', // 'player' | 'npc' | 'enemy' | 'companion'
      hpVisible: combatant.hpVisible !== false, // HP visible to players (default true)
      deathSaves: combatant.deathSaves || { successes: 0, failures: 0 }, // Death saving throws
      peerId: combatant.peerId || null,
      playerName: combatant.playerName || null,
      ownerId: combatant.ownerId || null, // Who owns this combatant (master or player peerId)
      isConcentrating: combatant.isConcentrating || false,
      rollMode: combatant.rollMode || 'normal', // 'normal' | 'adv' | 'dis'
      actionUsed: combatant.actionUsed || false,
      bonusActionUsed: combatant.bonusActionUsed || false,
      reactionUsed: combatant.reactionUsed || false,
      dashed: combatant.dashed || false,
      hitDice: combatant.hitDice && typeof combatant.hitDice.total === 'number' && combatant.hitDice.total > 0
        ? { total: combatant.hitDice.total, remaining: combatant.hitDice.remaining ?? combatant.hitDice.total, sides: [6, 8, 10, 12].includes(combatant.hitDice.sides) ? combatant.hitDice.sides : 8 }
        : { total: 0, remaining: 0, sides: 8 },
      famiglioDiName: combatant.famiglioDiName || null, // "Famiglio di [name]" in combat UI
      coinPouch: combatant.coinPouch && typeof combatant.coinPouch === 'object'
        ? { pp: Math.max(0, parseInt(combatant.coinPouch.pp, 10) || 0), gp: Math.max(0, parseInt(combatant.coinPouch.gp, 10) || 0), ep: Math.max(0, parseInt(combatant.coinPouch.ep, 10) || 0), sp: Math.max(0, parseInt(combatant.coinPouch.sp, 10) || 0), cp: Math.max(0, parseInt(combatant.coinPouch.cp, 10) || 0) }
        : { pp: 0, gp: 0, ep: 0, sp: 0, cp: 0 },
    };
    
    setState(prev => ({
      ...prev,
      combatants: [...prev.combatants, newCombatant],
    }));
    
    return newCombatant;
  }, []);

  // Update combatant
  const updateCombatant = useCallback((id, updates) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          const updated = { ...c, ...updates };
          // Auto-KO if HP <= 0
          updated.isKO = updated.hp <= 0;
          return updated;
        }
        return c;
      }),
    }));
  }, []);

  // Delete combatant
  const deleteCombatant = useCallback((id) => {
    setState(prev => {
      const newCombatants = prev.combatants.filter(c => c.id !== id);
      // Adjust currentTurnIndex if needed
      let newIndex = prev.currentTurnIndex;
      if (newIndex >= newCombatants.length) {
        newIndex = 0;
      }
      return {
        ...prev,
        combatants: newCombatants,
        currentTurnIndex: newIndex,
      };
    });
  }, []);

  // Modify HP (damage goes to Temp HP first)
  const modifyHp = useCallback((id, amount) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          let newHp = c.hp;
          let newTempHp = c.tempHp || 0;
          
          if (amount < 0) {
            // Damage: first reduce temp HP, then regular HP (concentration check is handled by UI)
            const damage = Math.abs(amount);
            if (newTempHp > 0) {
              const tempDamage = Math.min(newTempHp, damage);
              newTempHp -= tempDamage;
              const remainingDamage = damage - tempDamage;
              newHp = Math.max(0, newHp - remainingDamage);
            } else {
              newHp = Math.max(0, newHp - damage);
            }
          } else {
            // Healing: only affects regular HP, not temp HP
            newHp = Math.min(c.maxHp, newHp + amount);
          }
          
          return {
            ...c,
            hp: newHp,
            tempHp: newTempHp,
            isKO: newHp <= 0,
            isConcentrating: c.isConcentrating,
          };
        }
        return c;
      }),
    }));
  }, []);

  // Set Temp HP
  const setTempHp = useCallback((id, tempHp) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          return { ...c, tempHp: Math.max(0, tempHp) };
        }
        return c;
      }),
    }));
  }, []);

  // Add status to combatant (status may have value: e.g. Rigenerazione = HP/turn, Sanguinante = damage/turn)
  const addStatus = useCallback((id, status) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          return {
            ...c,
            statuses: [...c.statuses, { 
              id: generateId(),
              name: status.name, 
              duration: status.duration ?? null,
              value: typeof status.value === 'number' && status.value > 0 ? status.value : undefined,
            }],
          };
        }
        return c;
      }),
    }));
  }, []);

  // Remove status from combatant
  const removeStatus = useCallback((combatantId, statusId) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === combatantId) {
          return {
            ...c,
            statuses: c.statuses.filter(s => s.id !== statusId),
          };
        }
        return c;
      }),
    }));
  }, []);

  // Set DoT
  const setDot = useCallback((id, dot) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          return { ...c, dot };
        }
        return c;
      }),
    }));
  }, []);

  const rollInitiativeFor = useCallback((combatant) => {
    const mode = combatant.rollMode || 'normal';
    const bonus = typeof combatant.initiativeBonus === 'number' ? combatant.initiativeBonus : 0;
    const r1 = rollD20();
    if (mode === 'adv') {
      const r2 = rollD20();
      return Math.max(r1, r2) + bonus;
    }
    if (mode === 'dis') {
      const r2 = rollD20();
      return Math.min(r1, r2) + bonus;
    }
    return r1 + bonus;
  }, []);

  // Roll initiative for a combatant using bonus and adv/dis
  const rollInitiative = useCallback((id) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id !== id) return c;
        return { ...c, initiative: rollInitiativeFor(c) };
      }),
    }));
  }, [rollInitiativeFor]);

  // Roll initiative for all combatants
  const rollAllInitiative = useCallback(() => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => ({ ...c, initiative: rollInitiativeFor(c) })),
      currentTurnIndex: 0,
      round: 1,
    }));
  }, [rollInitiativeFor]);

  // Roll initiative only for NPCs and enemies (players/companions keep current)
  const rollInitiativeNpcAndEnemies = useCallback(() => {
    const isNpcOrEnemy = (c) => c.isNPC || c.isEnemy || c.combatantType === 'npc' || c.combatantType === 'enemy';
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c =>
        isNpcOrEnemy(c) ? { ...c, initiative: rollInitiativeFor(c) } : c
      ),
      currentTurnIndex: 0,
      round: 1,
    }));
  }, [rollInitiativeFor]);

  const shortRest = useCallback((id = null) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (id && c.id !== id) return c;
        return {
          ...c,
          actionUsed: false,
          bonusActionUsed: false,
          reactionUsed: false,
          dashed: false,
        };
      }),
    }));
  }, []);

  const longRest = useCallback((id = null) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (id && c.id !== id) return c;
        const slots = c.spellSlots || {};
        const resetSlots = Object.fromEntries(
          Object.entries(slots).map(([lvl, v]) => [
            lvl,
            v && typeof v.max === 'number' ? { ...v, used: 0 } : v,
          ])
        );
        // Ripristino dadi vita: recuperi metà dei dadi totali (arrotondato per difetto)
        const hd = c.hitDice && typeof c.hitDice.total === 'number' ? c.hitDice : { total: 0, remaining: 0, sides: 8 };
        const recovered = Math.floor(hd.total / 2);
        const newRemaining = Math.min(hd.total, (hd.remaining ?? hd.total) + recovered);
        return {
          ...c,
          hp: c.maxHp,
          tempHp: 0,
          isKO: false,
          deathSaves: { successes: 0, failures: 0 },
          statuses: [],
          dot: null,
          isConcentrating: false,
          actionUsed: false,
          bonusActionUsed: false,
          reactionUsed: false,
          dashed: false,
          spellSlots: resetSlots,
          hitDice: { ...hd, remaining: newRemaining },
        };
      }),
    }));
  }, []);

  // Next turn - returns info about what happened
  const nextTurn = useCallback(() => {
    const events = [];
    
    setState(prev => {
      if (prev.combatants.length === 0) return prev;
      
      const sorted = [...prev.combatants].sort((a, b) => b.initiative - a.initiative);
      let newIndex = (prev.currentTurnIndex + 1) % sorted.length;
      let newRound = prev.round;
      const isNewRound = newIndex === 0;
      
      // If we've gone through everyone, increment round
      if (isNewRound) {
        newRound += 1;
      }
      
      // Get the combatant whose turn is starting
      const activeCombatant = sorted[newIndex];
      
      // Process combatants
      const updatedCombatants = prev.combatants.map(c => {
        let updated = { ...c };

        // Reaction resets every round
        if (isNewRound) {
          updated.reactionUsed = false;
        }
        
        // Only process DoT and status for the active combatant
        if (c.id === activeCombatant.id) {
          // Reset turn economy for active combatant at start of their turn
          updated.actionUsed = false;
          updated.bonusActionUsed = false;
          updated.dashed = false;

          // Apply DoT damage
          if (updated.dot && updated.dot.damage > 0) {
            updated.hp = Math.max(0, updated.hp - updated.dot.damage);
            updated.isKO = updated.hp <= 0;
            events.push({ type: 'dot', combatant: c.name, damage: updated.dot.damage });
            
            // Decrement DoT duration if set
            if (updated.dot.duration !== null) {
              updated.dot = {
                ...updated.dot,
                duration: updated.dot.duration - 1,
              };
              if (updated.dot.duration <= 0) {
                updated.dot = null;
              }
            }
          }

          // Regeneration: at start of turn, restore HP (value = PF per turno)
          const regenStatus = updated.statuses.find(s => s.name === 'Rigenerazione' && s.value > 0);
          if (regenStatus && updated.hp < updated.maxHp) {
            const heal = Math.min(regenStatus.value, updated.maxHp - updated.hp);
            if (heal > 0) {
              updated.hp = updated.hp + heal;
              updated.isKO = false;
              events.push({ type: 'regen', combatant: c.name, amount: heal });
            }
          }

          // Sanguinante (Bleeding): at start of turn, take damage (value = danno per turno)
          const bleedStatus = updated.statuses.find(s => s.name === 'Sanguinante' && s.value > 0);
          if (bleedStatus) {
            updated.hp = Math.max(0, updated.hp - bleedStatus.value);
            updated.isKO = updated.hp <= 0;
            events.push({ type: 'bleed', combatant: c.name, damage: bleedStatus.value });
          }
          
          // Decrement status durations
          updated.statuses = updated.statuses.map(s => {
            if (s.duration !== null) {
              const newDuration = s.duration - 1;
              if (newDuration <= 0) {
                events.push({ type: 'statusExpired', combatant: c.name, status: s.name });
                return null;
              }
              return { ...s, duration: newDuration };
            }
            return s;
          }).filter(Boolean);
        }
        
        return updated;
      });
      
      events.push({ type: 'turnChange', combatant: activeCombatant.name });
      
      return {
        ...prev,
        combatants: updatedCombatants,
        currentTurnIndex: newIndex,
        round: newRound,
      };
    });
    
    return events;
  }, []);

  // Reset combat
  const resetCombat = useCallback(() => {
    setState(getInitialState());
  }, []);

  // Export data
  const exportData = useCallback(() => {
    return JSON.stringify({
      combatants: state.combatants,
      currentTurnIndex: state.currentTurnIndex,
      round: state.round,
      notes: state.notes ?? '',
      exportedAt: new Date().toISOString(),
    }, null, 2);
  }, [state]);

  // Import data
  const importData = useCallback((jsonString) => {
    try {
      const data = JSON.parse(jsonString);
      if (!data.combatants || !Array.isArray(data.combatants)) {
        throw new Error('Invalid data format');
      }
      setState({
        combatants: data.combatants,
        currentTurnIndex: data.currentTurnIndex || 0,
        round: data.round || 1,
        notes: data.notes ?? '',
      });
      return true;
    } catch (e) {
      console.error('Error importing data:', e);
      return false;
    }
  }, []);

  // Toggle KO manually
  const toggleKO = useCallback((id) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          const newKO = !c.isKO;
          // Reset death saves when recovering from KO
          const newDeathSaves = newKO ? (c.deathSaves || { successes: 0, failures: 0 }) : { successes: 0, failures: 0 };
          return { ...c, isKO: newKO, deathSaves: newDeathSaves };
        }
        return c;
      }),
    }));
  }, []);

  // Update death saving throws
  const updateDeathSaves = useCallback((id, type) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          const deathSaves = { ...c.deathSaves };
          if (type === 'success') {
            deathSaves.successes = Math.min(3, deathSaves.successes + 1);
            // If 3 successes, stabilize (HP = 1, no longer KO)
            if (deathSaves.successes >= 3) {
              return { 
                ...c, 
                hp: 1, 
                isKO: false, 
                deathSaves: { successes: 0, failures: 0 } 
              };
            }
          } else if (type === 'failure') {
            deathSaves.failures = Math.min(3, deathSaves.failures + 1);
            // If 3 failures, character dies
            if (deathSaves.failures >= 3) {
              return { ...c, deathSaves, hp: 0, isKO: true };
            }
          }
          return { ...c, deathSaves };
        }
        return c;
      }),
    }));
  }, []);

  // Reset death saves
  const resetDeathSaves = useCallback((id) => {
    setState(prev => ({
      ...prev,
      combatants: prev.combatants.map(c => {
        if (c.id === id) {
          return { ...c, deathSaves: { successes: 0, failures: 0 } };
        }
        return c;
      }),
    }));
  }, []);

  const setNotes = useCallback((value) => {
    setState(prev => ({ ...prev, notes: typeof value === 'function' ? value(prev.notes ?? '') : value }));
  }, []);

  return {
    combatants: sortedCombatants,
    currentTurnIndex: state.currentTurnIndex,
    currentCombatant,
    round: state.round,
    addCombatant,
    updateCombatant,
    deleteCombatant,
    modifyHp,
    setTempHp,
    addStatus,
    removeStatus,
    setDot,
    rollInitiative,
    rollAllInitiative,
    rollInitiativeNpcAndEnemies,
    shortRest,
    longRest,
    nextTurn,
    resetCombat,
    exportData,
    importData,
    toggleKO,
    updateDeathSaves,
    resetDeathSaves,

    notes: state.notes ?? '',
    setNotes,
  };
};
